import { APP_FORM_OPTIONS, OPTION_VALUES } from "@ipru-common/constants/InputValues";
import { leadParams } from "@ipru-common/constants/lead";
import CalculatorFormStore from "@ipru-common/store/CalculatorFormStore";
import QuoteFormStore from "@ipru-common/store/QuoteFormStore";
import { STATES } from "@ipru-common/constants/state";
import { getAge } from "@ipru-common/libs/ValidationSchemas/CustomRules";
import { RIDER_TYPES } from "@ipru-common/constants/SignatureUlipRider";
import { REGEX } from "@ipru-common/constants/regex";
import { getProductByKey } from "../SavingsAppForm/SavingAppFormsUtils";
import { PRODUCT_TYPES } from "@ipru-common/constants/products";
import { CURRENCY_SIGN, GIFT_PRO_ONLINE_DISCOUNT_PCT } from "@ipru-common/constants/SiteConstants";
import {
  compactNumberForm,
  getCurrentProductFromMeta,
  getProductDetails,
  isProductGppFlexi,
  isProductPnG,
  isProductSaralPension,
  isProtectionPlan,
} from "../CommonFunctions";
import DeathBenefitsJson from "@ipru-common/constants/JSON/DeathBenefitsTable.json";
import { PRODUCT_KEYS, PRODUCT_NAMES } from "@ipru-common/constants/products";
import { signatureAssuredTaxSavingMapper } from "../TaxSavingMapper/SignatureAssuredTaxSavingMapper";
import { signatureTaxSavingMapper } from "../TaxSavingMapper/SignatureTaxSavingMapper";
import { signatureSipPlusTaxSavingMapper } from "../TaxSavingMapper/SignatureSipPlusTaxSavingMapper";

export const calculateAnnualPremium = ({ invest, paymentFrequency } = {}) => {
  const AnnualPremiums = {
    [OPTION_VALUES.PAYMENT_FREQUENCY.YEARLY]: () => invest,
    [OPTION_VALUES.PAYMENT_FREQUENCY.HALF_YEARLY]: () => invest * 2,
    [OPTION_VALUES.PAYMENT_FREQUENCY.MONTHLY]: () => invest * 12,
  };

  return AnnualPremiums?.[paymentFrequency]?.() || 0;
};

export const calculateCapitalGuaranteePremium = ({ invest, split } = {}) => {
  if (!split) return;
  return Math.ceil((Number(invest) * Number(split[0])) / 100);
};

export const calculateMonthlyPremium = ({ invest, paymentFrequency }) => {
  const MonthlyPremiums = {
    [OPTION_VALUES.PAYMENT_FREQUENCY.YEARLY]: () => invest / 12,
    [OPTION_VALUES.PAYMENT_FREQUENCY.HALF_YEARLY]: () => invest / 6,
    [OPTION_VALUES.PAYMENT_FREQUENCY.MONTHLY]: () => invest,
  };

  return MonthlyPremiums?.[paymentFrequency]?.() || 0;
};

export const calculateTotalPremium = ({ invest, paymentFrequency, ppt }) => {
  const annualPremium = calculateAnnualPremium({ invest, paymentFrequency });
  const totalPremium = annualPremium * ppt;
  return totalPremium;
};

export const findOriginalIndex = (allocatedFunds, fundName) => {
  const index = allocatedFunds.findIndex((fund) => fund.Fund === fundName);
  return index !== -1 ? allocatedFunds[index].originalIndex : -1;
};

export const findFundByName = (allocatedFunds, fundName) => {
  return allocatedFunds.find((fund) => fund.Fund === fundName) || -1;
};

export const calculateTaxSavingsUnder80C = ({ onlineResponse, roundOff = true }) => {
  const { paymentFrequency, riderPremium } = QuoteFormStore.getQuoteFormData();
  let invest = onlineResponse?.PremiumSummary?.ModalPremium + riderPremium;

  if (isProtectionPlan()) {
    invest = onlineResponse?.PremiumSummary?.TotalFirstPremiumShow;
  }

  const annualPremium = (calculateAnnualPremium({ invest, paymentFrequency }) * 31.2) / 100;

  //Round off flag is used to eliminate extra tax amount accumulated over ppt
  return Math.min(roundOff ? Math.round(annualPremium) : annualPremium, 46800);
};

/**
 * @returns {CalculatedData}
 */
export const getCalculatedData = ({ onlineResponse, offlineResponse, ridersPremium, t }) => {
  let { invest, paymentFrequency, ppt, pastPerformancePercent, paymentOption } = QuoteFormStore.getQuoteFormData();

  if (isProtectionPlan()) {
    invest = onlineResponse?.PremiumSummary?.TotalFirstPremiumShow;
  }

  const onlinePremiumSummary = onlineResponse?.PremiumSummary;

  const offlinePremiumSummary = offlineResponse ? offlineResponse.PremiumSummary : null;

  const annualPremium = calculateAnnualPremium({ invest, paymentFrequency });

  const totalPremium = annualPremium * ppt;

  const online4Arr = onlinePremiumSummary?.PurchaseAnnuity6;
  const offline4Arr = offlinePremiumSummary ? offlinePremiumSummary.PurchaseAnnuity6 : 0;

  const online8Arr = onlinePremiumSummary?.PurchaseAnnuity10;
  const offline8Arr = offlinePremiumSummary ? offlinePremiumSummary.PurchaseAnnuity10 : 0;

  const onlinePastPerformance = onlinePremiumSummary?.EstimatedMaturityAddition10;

  const taxSavingUnder80C = calculateTaxSavingsUnder80C({ onlineResponse });

  const taxSavedOnReturn4Arr = calculateTaxSavedOnReturns({
    annualPremium,
    totalReturn: online4Arr,
    totalPremium,
    paymentOption,
  });

  const taxSavedOnReturn8Arr = calculateTaxSavedOnReturns({
    annualPremium,
    totalReturn: online8Arr,
    totalPremium,
    paymentOption,
  });

  const taxSavedOnReturnPastPerformance = calculateTaxSavedOnReturns({
    annualPremium,
    totalReturn: onlinePastPerformance,
    totalPremium,
    paymentOption,
  });

  const totalTaxSavingUnder80C = taxSavingUnder80C * ppt;

  const product = getCurrentProductFromMeta();

  const isGiftProProduct = product === PRODUCT_KEYS.GIFTPRO;

  const giftProData = isGiftProProduct ? calculateDataForGiftProQoute(onlineResponse, t) : {};

  const calculatedTaxDataForGiftPro = isGiftProProduct ? calculateTaxSavingsValue(giftProData?.overallReturns, t) : {};

  /**
   * @type {CalculatedData}
   */
  const calculatedData = {
    riders: ridersPremium,
    ...(isGiftProProduct ? { giftProQouteData: onlineResponse, giftProData } : {}),
    ...(isProductPnG() ? { pngQuoteData: onlineResponse?.PremiumSummary } : {}),
    ...(isProductSaralPension() ? { saralQuoteResponse: onlineResponse?.PremiumSummary } : {}),
    ...(isProductGppFlexi() ? { gppQuoteData: onlineResponse } : {}),
    calculatedTaxDataForGiftPro,
    taxSavingUnder80C,
    totalTaxSavingUnder80C,
    returns: {
      arr: [
        {
          rateOfInterest: 8,
          onlineReturn: online8Arr,
          offlineReturn: offline8Arr,
          extraAmount: online8Arr - offline8Arr,
          taxSavedOnReturn: taxSavedOnReturn8Arr,
          totalTaxSaved: taxSavedOnReturn8Arr + totalTaxSavingUnder80C,
        },
        {
          rateOfInterest: 4,
          onlineReturn: online4Arr,
          offlineReturn: offline4Arr,
          extraAmount: online4Arr - offline4Arr,
          totalTaxSaved: taxSavedOnReturn4Arr + totalTaxSavingUnder80C,
          taxSavedOnReturn: taxSavedOnReturn4Arr,
        },
      ],
      pastPerformance: {
        rateOfInterest: Number(pastPerformancePercent),
        taxSavedOnReturn: taxSavedOnReturnPastPerformance,
        totalTaxSaved: taxSavedOnReturnPastPerformance + totalTaxSavingUnder80C,
        onlineReturn: onlinePastPerformance,
      },
    },
  };
  return calculatedData;
};

export const calculateTaxSavedOnReturns = ({ annualPremium, totalReturn, totalPremium, paymentOption }) => {
  let productName = getProductDetails().productName || PRODUCT_NAMES.Signature;

  const GET_TAX_MAPPER = {
    [PRODUCT_NAMES.Signature]: signatureTaxSavingMapper,
    [PRODUCT_NAMES.SignatureAssured]: signatureAssuredTaxSavingMapper,
    [PRODUCT_NAMES.SignatureSecure]: signatureAssuredTaxSavingMapper,
    [PRODUCT_NAMES.SipPlus]: signatureSipPlusTaxSavingMapper,
  };

  const taxSavedCalculator = GET_TAX_MAPPER[productName] || (() => 0);

  return taxSavedCalculator({ annualPremium, totalReturn, totalPremium, paymentOption });
};

export const generateSmartKidLeadData = (defaultLeadData = {}) => {
  const { invest, term, ppt, lifeCover, paymentFrequency } = QuoteFormStore.getQuoteFormData();
  const annualPremium = calculateAnnualPremium({ invest, paymentFrequency });

  const { child_name, child_dob, user_dob, skip_suitability_check, trai_flag } = CalculatorFormStore.getCalFormData();

  const productCode = ppt === 1 ? "UA9" : "UA8";

  const additionalDetails = JSON.parse(defaultLeadData.LmsAdditionalDetails || {});
  delete additionalDetails?.ped_prsn_edu;

  return {
    [leadParams.LMS_FIRSTNAME]: child_name,
    [leadParams.LMS_LASTNAME]: "",
    [leadParams.LMS_DOB]: user_dob,
    [leadParams.LMS_PRODUCT_CODE]: productCode,
    [leadParams.LMS_PRODUCT_SPECIFIC]: "",
    [leadParams.LMS_PREMIUM]: annualPremium,
    [leadParams.LMS_POLICYTERM]: term,
    [leadParams.LMS_SUMASSURED]: lifeCover,
    [leadParams.LMS_DEATHBENEFIT]: "",
    [leadParams.LMS_TYPEOFPLAN]: "LumpSum",
    [leadParams.LMS_PAYOUTOPTION]: "",
    [leadParams.LMS_POLICYFREQUENCY]: paymentFrequency,
    [leadParams.LMS_PREMIUMTERM]: ppt,
    [leadParams.LMS_CIBENEFIT]: "",
    [leadParams.LMS_STAFF]: "No",
    [leadParams.TERM_CRITICAL_ILLNESS]: "Self",
    [leadParams.LMS_PAGE_NAME]: "",
    [leadParams.LMS_TOBACCO]: "",
    [leadParams.LMS_COLUMN_2]: child_name,
    [leadParams.LMS_COLUMN_4]: "",
    [leadParams.LMS_COLUMN_6]: "Self",
    [leadParams.LMS_COLUMN_9]: "",
    [leadParams.LMS_COLUMN_11]: skip_suitability_check ? "YES" : "NO",
    [leadParams.LMS_COLUMN_12]: trai_flag ? "YES" : "NO",
    [leadParams.LMS_PAGE_NAME]: "savings : ulip-insurance-plans : icici-pru-smart-kid",
    [leadParams.LMS_PAGENAME]: "",
    [leadParams.LMS_ADDITIONAL_DETAILS]: JSON.stringify({
      ...additionalDetails,
      lms_child_dob: child_dob,
      lms_child_age: child_dob ? getAge(child_dob) : "",
    }),
    [leadParams.LMS_pageURL]: "https://www.icicilife.com/child-insurance/smart-kid-child-savings-plan-calculator.html",
  };
};

export const calculateAgeByDob = (dob) => {
  if (!dob) {
    return -1;
  }
  const [day, month, year] = dob.split("/");
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getStateIdByName = (stateName, stateJson) => {
  const state = stateJson?.find((item) => item.name?.toLowerCase() === stateName?.toLowerCase());
  return state ? state?.id : "";
};

export const getStateNameById = ({ stateId }) => {
  const state = STATES?.find((state) => Number(state.id) === Number(stateId));
  return state ? state?.name : "";
};

export const calculateSAMultiple = ({ age, PremiumPaymentTerm }) => {
  return PremiumPaymentTerm === 1 && age > 35 ? 1.25 : 10;
};

/**
 * @type {(number :string)=>({
 * lastFourDigits,
 * maskedAadhar,
 * aadharType,
 * })}
 */
export const getMaskedAadhaar = (number) => {
  let strAadhar = String(number);

  if (!/^\d+$/.test(strAadhar)) {
    return {
      lastFourDigits: "",
      maskedAadhar: "",
      aadharType: "",
    };
  }

  const numberArray = strAadhar?.split("") || [];
  const lastFourDigits = strAadhar?.slice(-4);
  const maskedAadhar = lastFourDigits.padStart(numberArray?.length, "12");
  const aadharType = numberArray?.length === 12 ? "Aadhar No" : numberArray?.length === 16 ? "Virtual ID" : "";

  return {
    lastFourDigits,
    maskedAadhar,
    aadharType,
  };
};

export const getOccupationName = (id) => {
  return APP_FORM_OPTIONS.OCCUPATION.find((o) => o.id === id)?.name || "";
};

export const isNegativePincode = (pincode, negativePincodes = []) => {
  const pincodeAsNumber = Number(pincode);

  let parsedNegativePincodes = Array.isArray(negativePincodes)
    ? negativePincodes
    : typeof negativePincodes === "string"
      ? JSON.parse(negativePincodes)
      : [];

  return parsedNegativePincodes.includes(pincodeAsNumber);
};

export const calculatePremiumBasedOnFrequency = ({ monthlyPremium, paymentFrequency }) => {
  if (paymentFrequency === OPTION_VALUES.PAYMENT_FREQUENCY.YEARLY) {
    return monthlyPremium * 12;
  }

  if (paymentFrequency === OPTION_VALUES.PAYMENT_FREQUENCY.HALF_YEARLY) {
    return monthlyPremium * 6;
  }

  return monthlyPremium;
};

export function findDisplayValueInOptions(idToFind, options) {
  const option = options?.find((option) => option?.id == idToFind) || null;
  return option?.name;
}

export const getUniqueId = () => {
  const date = new Date();
  const id = date.getTime();
  return String(id);
};

export function generateNewSectionWithUpdatedFields(fields, length) {
  let finalFieldsArray = [];
  Array.from({ length: length })?.map((_, index) => {
    fields.map((currField) => {
      let field = {
        type: currField.type,
        props: {
          name: currField?.props?.name?.replace("[]", `[${index}]`),
          label: currField?.props?.label,
          ...(currField?.props?.options && { options: currField?.props?.options }),
        },
      };
      finalFieldsArray.push(field);
    });
  });
  return {
    fields: finalFieldsArray,
  };
}

export const isWholeLifePolicyTerm = (term) => {
  return term > 30;
};
export function getTitleFromRiderType(sectionName) {
  let text = "";
  switch (sectionName) {
    case RIDER_TYPES.WOP:
      text = "WOP Rider (exclusive of GST)";
      break;
    case RIDER_TYPES.ADBY:
      text = "ADBR";
      break;
    case RIDER_TYPES.ACPD:
      text = "ATPD";
      break;
    case RIDER_TYPES.BTBB:
      text = "Term Booster benefit option";
      break;
  }
  return text;
}

export function getPnGTitleFromRiderType(sectionName) {
  let text = "";
  switch (sectionName) {
    case RIDER_TYPES.WOP:
      text = "WOP Rider (exclusive of GST)";
      break;
    case RIDER_TYPES.ADBY:
      text = "Accidental Death Rider";
      break;
    case RIDER_TYPES.ACPD:
      text = "Accidental Disability Rider";
      break;
  }
  return text;
}

export const getDefaultUID = () => {
  const mapper = {
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: 57437,
    [PRODUCT_KEYS.GIFT_CITY]: 57437,
    default: 480,
  };
  return mapper[getCurrentProductFromMeta()] ?? mapper.default;
};

export const cleanUid = (uidParam, defaultUID = 480) => {
  const cleanUid = uidParam?.split("?")?.[0];
  const validUid = REGEX.ONLY_NUMBER.test(cleanUid);
  return String(validUid ? cleanUid : defaultUID);
};

export const checkIsAnnuityProduct = () => {
  const productMetaName = getCurrentProductFromMeta();
  const currentProduct = getProductByKey(productMetaName);

  return currentProduct?.type === PRODUCT_TYPES.retirementInsurancePlans;
};
//Calculating annual returns for Incresing Deathbenefit option
let calculateIncreasingIncome = (income, guaranteedCashBenefit, incomeFor, customiseIncome) => {
  if (customiseIncome === "Level") return income * incomeFor + guaranteedCashBenefit;
  else {
    let start = income;
    let percent = 0.05;
    let increasingArray = [];
    let interest = Math.ceil(start * percent);
    let sum = 0;

    increasingArray.push(start);

    for (let i = 0; i < incomeFor - 1; i++) {
      let value = start + interest;
      start = value;
      increasingArray.push(value);
    }

    increasingArray.forEach((num) => {
      sum += num;
    });

    return sum + guaranteedCashBenefit;
  }
};

const calculateLifeCoverBenefit = (invest, paymentFrequency, incomeBooster, age) => {
  const annualPremium = calculateAnnualPremium({
    invest: invest,
    paymentFrequency: paymentFrequency,
  });

  let lcbMultiplier = DeathBenefitsJson[incomeBooster][age];

  return annualPremium * lcbMultiplier;
};
//Calculations fro qoutePage data

const calculateDataForGiftProQoute = (onlineResponse, t) => {
  const { incomeFor, incomeForFrequency, customiseIncome, invest, paymentFrequency, incomeBooster } =
    QuoteFormStore.getQuoteFormData();
  const { age } = CalculatorFormStore.getCalFormData();

  const incomeInt = parseInt(onlineResponse?.PremiumSummary?.GuaranteedIncome) || 0;
  const guaranteedCashBenefit = parseInt(onlineResponse?.PremiumSummary?.GuaranteedCashBenefit) || 0;
  const alsoGet = calculateLifeCoverBenefit(invest, paymentFrequency, incomeBooster, age);
  const overallReturns = calculateIncreasingIncome(incomeInt, guaranteedCashBenefit, incomeFor, customiseIncome);
  const income = incomeForFrequency === OPTION_VALUES.PAYMENT_FREQUENCY.YEARLY ? incomeInt : incomeInt / 12;

  const onlineDiscount = (overallReturns * GIFT_PRO_ONLINE_DISCOUNT_PCT) / 100;
  const withDiscount = `${GIFT_PRO_ONLINE_DISCOUNT_PCT}%`;
  const withoutDiscount = `${GIFT_PRO_ONLINE_DISCOUNT_PCT}%`;
  const withDiscountValue = compactNumberForm(overallReturns - onlineDiscount, 2);
  const withoutDiscountValue = compactNumberForm(overallReturns, 2);
  const onlineDiscountDataGiftPro = {
    title: t("giftPro.discountsPopup.title"),
    extraDiscount: compactNumberForm(onlineDiscount, 2),
    extraDiscountText: t("giftPro.discountsPopup.extraDiscount", {
      extraDiscount: compactNumberForm(onlineDiscount, 2),
    }),
    withDiscountText: t("giftPro.discountsPopup.withDiscount", { withDiscount }),
    withoutDiscountText: t("giftPro.discountsPopup.withoutDiscount", { withoutDiscount }),
    withDiscountValue: withDiscountValue,
    withoutDiscountValue: withoutDiscountValue,
    btnText: t("giftPro.discountsPopup.btnText"),
  };

  return {
    overallReturns,
    income: compactNumberForm(income, 2),
    guaranteedCashBenefit: compactNumberForm(guaranteedCashBenefit, 2),
    guaranteedCashBenefitNumber: guaranteedCashBenefit,
    onlineDiscount,
    onlineDiscountDataGiftPro,
    alsoGet,
  };
};
const calculateTaxSavingsValue = (overallReturns, t) => {
  const { invest, paymentOption, paymentFrequency, ppt } = QuoteFormStore.getQuoteFormData();
  const { age } = CalculatorFormStore.getCalFormData();

  const annualPremium = calculateAnnualPremium({
    invest: invest,
    paymentFrequency: paymentFrequency,
  });

  const annualPremiumWithRiderPremium = annualPremium || 0;
  const totalAmountInvested = annualPremiumWithRiderPremium * (ppt || 0) || 0;
  let taxSavingUnder80C = 0.312 * annualPremiumWithRiderPremium || 0;
  taxSavingUnder80C = taxSavingUnder80C > 46800 ? 46800 : taxSavingUnder80C || 0;

  const totalTaxSavingUnder80C = taxSavingUnder80C * ppt || 0;
  const totalReturnAmount = overallReturns || 0; //Need to update the value

  let taxSavedOnReturn = annualPremiumWithRiderPremium > 249999 ? 0 : 0.312 * (totalReturnAmount - totalAmountInvested);

  const totalTaxSavings = taxSavedOnReturn + totalTaxSavingUnder80C;

  const header = [
    "",
    t("smartKid.quotePage.taxSavingPopup.amountInvested"),
    t("smartKid.quotePage.taxSavingInnerTitle"),
    t("smartKid.quotePage.taxSavingPopup.taxOnReturn"),
    t("smartKid.quotePage.taxSavingPopup.totalTax"),
  ];

  const rows = [
    {
      label: t("smartKid.quotePage.taxSavingPopup.1year"),
      amountInvested: `${CURRENCY_SIGN}${compactNumberForm(annualPremiumWithRiderPremium, 2)}`,
      taxSaving: `${CURRENCY_SIGN}${compactNumberForm(taxSavingUnder80C, 2)}<sup>*</sup>`,
      taxSavedOnReturn: "-",
    },
    {
      label: t("smartKid.quotePage.taxSavingPopup.overallYears", { totalYears: ppt }),
      amountInvested: `${CURRENCY_SIGN}${compactNumberForm(totalAmountInvested, 2)}`,
      taxSaving: `${CURRENCY_SIGN}${compactNumberForm(totalTaxSavingUnder80C, 2)}<sup>*</sup>`,
      taxSavedOnReturn:
        paymentOption == OPTION_VALUES.PAYMENT_OPTIONS.ONE_TIME && getAge(age) > 36
          ? "-"
          : `${CURRENCY_SIGN}${compactNumberForm(taxSavedOnReturn, 2)}`,
    },
  ];

  return {
    annualPremiumWithRiderPremium,
    totalAmountInvested,
    taxSavingUnder80C,
    totalTaxSavingUnder80C,
    taxSavedOnReturn,
    totalTaxSavings,
    header: header,
    rows: rows,
  };
};
